/* eslint-disable global-require, no-underscore-dangle */

const eventing = require('../../helpers/eventing');
const reemit = require('../../helpers/reemit');
const destroy = require('../../helpers/destroyObj');

const DefaultNativeVideoElementWrapperFactory = require('./NativeVideoElementWrapper/NativeVideoElementWrapper');
const DefaultPluginVideoElementWrapper = require('./plugin_video_element_wrapper');
const DefaultOTPlugin = require('../../otplugin/otplugin');
const DefaultLogging = require('../../ot/logging');

/**
 * VideoElementFacadeFactory DI Container for VideoElementFacade
 *
 * @package
 * @param {Object} [dependencies]
 * @param {typeof NativeVideoElementWrapper} [dependencies.NativeVideoElementWrapper]
 * @param {typeof PluginVideoElementWrapper} [dependencies.PluginVideoElementWrapper]
 * @param {Object} [dependencies.OTPlugin]
 * @param {Object} [dependencies.logging]
 * @return {typeof VideoElementFacade}
 */
function VideoElementFacadeFactory({
  NativeVideoElementWrapper = DefaultNativeVideoElementWrapperFactory(),
  PluginVideoElementWrapper = DefaultPluginVideoElementWrapper,
  OTPlugin = DefaultOTPlugin,
  logging = DefaultLogging,
} = {}) {
  const VideoElementWrapper = OTPlugin.isInstalled() ?
    PluginVideoElementWrapper : NativeVideoElementWrapper;
  const defaultAudioVolume = 50;

  /**
   * Create a new VideoElementFacade
   *
   * Used to simplify the strategy between Plugin and Native
   *
   * Note: Could VideoElementFacade not exist? Could it just be an expected interface
   * for VideoElementWrapper which could be either PluginVideoElementWrapper or
   * NativeVideoElementWrapper
   *
   * @package
   * @class
   * @param {Object} [config]
   * @param {String} [config.fallbackText] displayed when WebRTC is not supported
   * @param {Boolean} [config.muted] initial mute setting
   * @param {String} [config.fitMode] fitMode a parameter passed to VideoElementWrapper
   * @param {Object} [config._inject] @todo use the DI system
   * @param {Function} [errorHandler=() => {}] error callback function
   */
  function VideoElementFacade({
    fallbackText = 'Sorry, Web RTC is not available in your browser',
    fitMode,
    muted, // used if it's a publisher
    _inject,
  } = {}, errorHandler = () => {}) {
    let _stream;
    let _preInitialisedVolume;

    // shouldn't this be an event rather than a callback on PluginVideoElementWrapper
    const orientationHandler = orientation => this.trigger('orientationChanged', orientation);

    const _videoElementWrapper = new VideoElementWrapper(
      { fallbackText, muted, _inject, fitMode },
      errorHandler,
      orientationHandler,
      defaultAudioVolume
    );

    eventing(this);

    reemit(
      ['videoDimensionsChanged', 'mediaStopped', 'videoElementCreated'],
      _videoElementWrapper,
      this
    );

    // Public Methods

    /**
     * The DOM Element
     * @return {Element}
    */
    this.domElement = () => _videoElementWrapper.domElement();
    this.videoWidth = () => Number(_videoElementWrapper[`video${this.isRotated() ? 'Height' : 'Width'}`]());
    this.videoHeight = () => Number(_videoElementWrapper[`video${this.isRotated() ? 'Width' : 'Height'}`]());
    this.aspectRatio = () => this.videoWidth() / this.videoHeight();
    this.isRotated = () => _videoElementWrapper.isRotated();
    this.orientation = (orientation) => {
      if (orientation !== undefined) {
        _videoElementWrapper.orientation(orientation);
      }
      return _videoElementWrapper.orientation();
    };
    this.audioChannelType = (type) => {
      if (type !== undefined) {
        _videoElementWrapper.audioChannelType(type);
      }
      return _videoElementWrapper.audioChannelType();
    };
    /**
     * @return {String} A data uri representing the current frame in PNG image format
    */
    this.imgData = () => _videoElementWrapper.imgData();
    /**
     * Append this.domElement() to parentDomElement
     * @param {Element} parentDomElement the target element to append to
     * @return {VideoElementFacade}
     */
    this.appendTo = (parentDomElement) => {
      _videoElementWrapper.appendTo(parentDomElement);
      return this;
    };
    this.isAudioBlocked = () => _videoElementWrapper.isAudioBlocked();
    this.playAudio = () => _videoElementWrapper.playAudio();
    /**
     * Bind webRtcStream
     * @param {MediaStream} webRtcStream
     * @param {Function} completion
     * @return {VideoElementFacade}
     */
    this.bindToStream = (webRtcStream, completion) => {
      _stream = webRtcStream;

      return _videoElementWrapper.bindToStream(webRtcStream)
        .catch(err => completion(err))
        .then(() => {
          if (typeof _preInitialisedVolume !== 'undefined') {
            try {
              this.setAudioVolume(_preInitialisedVolume);
            } catch (e) {
              logging.warn(`bindToStream ${e}`);
            }
            _preInitialisedVolume = undefined;
          }

          _videoElementWrapper.on('aspectRatioAvailable',
            this.trigger.bind(this, 'aspectRatioAvailable'));

          completion(null);
        });
    };
    this.unbindStream = () => {
      if (!_stream) { return this; }

      _stream.onended = null;
      _stream = null;
      _videoElementWrapper.unbindStream();
      return this;
    };
    this.setAudioVolume = value =>
      _videoElementWrapper.setAudioVolume(Number((value / 100).toFixed(2)));
    this.getAudioVolume = () => Math.round(_videoElementWrapper.getAudioVolume() * 100);
    this.getAudioInputLevel = () => _videoElementWrapper.getAudioInputLevel();
    this.whenTimeIncrements = (callback, context) => {
      _videoElementWrapper.whenTimeIncrements(callback, context);
      return this;
    };

    /**
     * Clean up and prevents further use of this instance
     */
    this.destroy = () => {
      this.off();
      _videoElementWrapper.destroy();
      destroy('VideoElementFacade', this);
    };
  }
  return VideoElementFacade;
}

module.exports = VideoElementFacadeFactory;




// WEBPACK FOOTER //
// ./src/js/helpers/video_element/index.js